import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink, Maximize2, Pause, Play, Volume2, VolumeX, X } from "lucide-react";
import { links, workItems } from "../../data/portfolio";
import { media } from "../../data/media";
import { useVimeoMeta } from "../../hooks/useVimeoMeta";
import { lockScroll } from "../../utils/scrollLock";
import { aspectLabels, kindFromAspect, viewerFrames } from "../../utils/work";
import { vimeoPageUrl, vimeoPlayerUrl } from "../../utils/vimeo";
import type { WorkItem } from "../../types/work";
type ProjectViewerProps = {
  item: WorkItem | null;
  onClose: () => void;
  onNavigate: (item: WorkItem) => void;
  /** Sequence the prev/next controls walk. Defaults to the homepage selection. */
  items?: WorkItem[];
};
function timecode(seconds: number) {
  if (!Number.isFinite(seconds)) return "00:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}
export function ProjectViewer({
  item,
  onClose,
  onNavigate,
  items = workItems
}: ProjectViewerProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const activeIndex = item ? items.findIndex(entry => entry.id === item.id) : -1;
  const meta = useVimeoMeta(item?.vimeoId);
  const aspect = item?.aspect ?? meta.aspect ?? "landscape";
  const title = item?.title ?? meta.title ?? "Untitled film";
  const kind = item?.kind ?? kindFromAspect(aspect);
  const cover = item ? meta.thumbnail ?? item.posterUrl ?? (item.plate ? (media as Record<string, string>)[item.plate] : undefined) ?? media.archivePlate : "";
  const sourceUrl = item?.vimeoId ? vimeoPageUrl(item.vimeoId) : links.sourceArchive;
  const step = useCallback((direction: 1 | -1) => {
    if (activeIndex < 0 || items.length < 2) return;
    const next = items[(activeIndex + direction + items.length) % items.length];
    onNavigate(next);
  }, [activeIndex, items, onNavigate]);
  useEffect(() => {
    if (!item) return;
    setPlaying(true);
    setMuted(true);
    setProgress(0);
    setCurrent(0);
    setDuration(0);
  }, [item]);
  useEffect(() => {
    if (!item) return;
    previousFocus.current = document.activeElement as HTMLElement | null;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key === "ArrowRight") {
        step(1);
        return;
      }
      if (event.key === "ArrowLeft") {
        step(-1);
        return;
      }
      if (event.key === " " && item.videoUrl) {
        event.preventDefault();
        setPlaying(value => !value);
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], iframe, [tabindex]:not([tabindex="-1"])'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    const releaseScroll = lockScroll();
    window.addEventListener("keydown", onKeyDown);
    return () => {
      releaseScroll();
      window.removeEventListener("keydown", onKeyDown);
      previousFocus.current?.focus();
    };
  }, [item, onClose, step]);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (playing) void video.play().catch(() => setPlaying(false));else video.pause();
  }, [playing, item]);
  useEffect(() => {
    const video = videoRef.current;
    if (video) video.muted = muted;
  }, [muted, item]);
  const onScrub = (event: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    const value = Number(event.target.value);
    setProgress(value);
    if (video && Number.isFinite(video.duration)) {
      video.currentTime = value / 100 * video.duration;
    }
  };
  const requestFullscreen = () => {
    const target = videoRef.current ?? shellRef.current;
    void target?.requestFullscreen?.().catch(() => undefined);
  };
  return <AnimatePresence data-id="element-463" data-mp="22731i1mwn4zo">
      {item && <motion.div ref={dialogRef} className="fixed inset-0 z-[95] flex flex-col bg-[#040606]/98 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label={`${title}, player`} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.32,
      ease: [0.16, 1, 0.3, 1]
    }} data-id="element-464" data-mp="2284g2g34q9m5">
          <header className="flex min-h-[72px] shrink-0 items-center justify-between gap-6 border-b border-white/10 px-5 md:px-8" data-id="element-465" data-mp="3hpr6nikqzari">
            <div className="min-w-0" data-id="element-466" data-mp="e8sfypmswo42">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#54f6c6]" data-id="element-467" data-mp="e7qxw17db1cu">
                {kind}{item.index ? ` / ${item.index}` : ""} · {aspectLabels[aspect]}
              </p>
              <p className="mt-1.5 truncate text-sm text-white" data-id="element-468" data-mp="4hxn32f1uq9n">{title}</p>
            </div>
            <button type="button" onClick={onClose} autoFocus data-cursor="Close" className="flex min-h-11 shrink-0 items-center gap-3 rounded-xl border border-white/15 px-4 font-mono text-[11px] uppercase tracking-[0.16em] text-white transition-colors hover:border-[#54f6c6] hover:text-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-469" data-mp="3vo8r97fcw757">
              Close <X className="h-4 w-4" data-id="element-470" data-mp="2cqm08r2owp8k" />
            </button>
          </header>

          <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 py-6 md:px-16" data-id="element-471" data-mp="2cohv65t21k6p">
            <button type="button" onClick={() => step(-1)} aria-label="Previous project" data-cursor="Prev" className="absolute left-2 z-10 hidden h-12 w-12 place-items-center rounded-xl border border-white/15 text-white/70 transition-colors hover:border-[#54f6c6] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6] md:grid" data-id="element-472" data-mp="2hncbn13juwnn">
              <ArrowLeft className="h-4 w-4" data-id="element-473" data-mp="3o6sqv04xr4rb" />
            </button>

            <AnimatePresence mode="wait" data-id="element-474" data-mp="3t4ltjj715gmu">
              <motion.div key={item.id} ref={shellRef} initial={{
            opacity: 0,
            scale: 0.97
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.99
          }} transition={{
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1]
          }} className={`relative overflow-hidden rounded-[20px] border border-white/10 bg-black ${viewerFrames[aspect]}`} data-id="element-475" data-mp="3t4c2rte5v5g3">
                {item.vimeoId ? <iframe src={vimeoPlayerUrl(item.vimeoId)} title={`${title}, video player`} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen className="h-full w-full border-0" data-id="element-476" data-mp="206ox44eklvll" /> : item.videoUrl ? <video ref={videoRef} src={item.videoUrl} poster={cover} className="h-full w-full object-contain" playsInline loop muted={muted} autoPlay onLoadedMetadata={event => setDuration(event.currentTarget.duration)} onTimeUpdate={event => {
              const video = event.currentTarget;
              setCurrent(video.currentTime);
              if (Number.isFinite(video.duration) && video.duration > 0) {
                setProgress(video.currentTime / video.duration * 100);
              }
            }} data-id="element-477" data-mp="fs51luasvaeb" /> : <div className="relative h-full w-full" data-id="element-478" data-mp="3c42ptehfeck1">
                    <img src={cover} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-45" data-id="element-479" data-mp="3c6otv453y9xi" />
                    <div className="absolute inset-0 film-lines opacity-40" data-id="element-480" data-mp="redaz1gq2qrc" />
                    <div className="absolute inset-0 grid place-items-center px-8 text-center" data-id="element-481" data-mp="rgg1dmlselwl">
                      <div data-id="element-482" data-mp="vi1khpowzwf8">
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#54f6c6]" data-id="element-483" data-mp="2fxraris3agvb">{aspectLabels[aspect]} frame reserved</p>
                        <p className="mx-auto mt-5 max-w-md text-sm leading-7 text-white/65 md:text-[15px]" data-id="element-484" data-mp="2bxp5z0r257bt">
                          The source portfolio doesn’t expose a direct file for this entry, so nothing has been substituted. Open the verified archive to watch it.
                        </p>
                        <a href={links.sourceArchive} target="_blank" rel="noreferrer" data-cursor="Open" className="mt-8 inline-flex min-h-11 items-center gap-3 bg-[#54f6c6] px-5 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[#05100d] transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-485" data-mp="1x8azc0ev9404">
                          Open verified archive <ExternalLink className="h-3.5 w-3.5" data-id="element-486" data-mp="3n0j3g23xzq2b" />
                        </a>
                      </div>
                    </div>
                  </div>}
                <span aria-hidden="true" className="pointer-events-none absolute left-4 top-4 rounded-lg bg-black/50 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/80 backdrop-blur-sm" data-id="element-487" data-mp="3i3sq3mjiv8kl">
                  {aspectLabels[aspect]}
                </span>
              </motion.div>
            </AnimatePresence>

            <button type="button" onClick={() => step(1)} aria-label="Next project" data-cursor="Next" className="absolute right-2 z-10 hidden h-12 w-12 place-items-center rounded-xl border border-white/15 text-white/70 transition-colors hover:border-[#54f6c6] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6] md:grid" data-id="element-488" data-mp="2zbi3l396dn8n">
              <ArrowRight className="h-4 w-4" data-id="element-489" data-mp="2fmphsf8md4fa" />
            </button>
          </div>

          <footer className="shrink-0 border-t border-white/10 px-5 py-4 md:px-8" data-id="element-490" data-mp="v45al4ln3haj">
            {!item.videoUrl && !item.vimeoId && <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45" data-id="element-491" data-mp="zxhx996rde0n">
                Transport controls activate once this frame has a source file. Open the verified archive to watch it.
              </p>}

            {item.vimeoId ? <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-492" data-mp="1etpblsvhjl80">
                <span className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.16em] text-white/55" data-id="element-493" data-mp="1itzir8v0khog">
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[#54f6c6] dot-blink" data-id="element-494" data-mp="14wug7iuie0xy" />
                  Streaming from Vimeo · play, sound, and fullscreen sit on the player
                </span>
                <a href={sourceUrl} target="_blank" rel="noreferrer" data-cursor="Open" className="flex min-h-11 items-center gap-2 rounded-xl border border-white/15 px-4 font-mono text-[11px] uppercase tracking-[0.14em] text-white/75 transition-colors hover:border-[#54f6c6] hover:text-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-495" data-mp="3uk25xrssic1a">
                  Watch on Vimeo <ExternalLink className="h-3.5 w-3.5" data-id="element-496" data-mp="2rrg5qcs657y6" />
                </a>
              </div> : <div className="flex flex-wrap items-center gap-4" data-id="element-497" data-mp="2msmpiclhz2ub">
              <div className="flex items-center gap-2" data-id="element-498" data-mp="2muhdjzs2fb2m">
                <button type="button" onClick={() => setPlaying(value => !value)} disabled={!item.videoUrl} title={item.videoUrl ? undefined : "No source file attached to this frame yet"} aria-label={playing ? "Pause" : "Play"} data-cursor={playing ? "Pause" : "Play"} className="grid h-11 w-11 place-items-center rounded-xl border border-white/15 text-white transition-colors hover:border-[#54f6c6] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-499" data-mp="2ipzxobotthx4">
                  {playing ? <Pause className="h-4 w-4" data-id="element-500" data-mp="tc0aa2gd9mss" /> : <Play className="h-4 w-4 fill-current" data-id="element-501" data-mp="y98s21xlroti" />}
                </button>
                <button type="button" onClick={() => setMuted(value => !value)} disabled={!item.videoUrl} title={item.videoUrl ? undefined : "No source file attached to this frame yet"} aria-label={muted ? "Sound on" : "Sound off"} className="grid h-11 w-11 place-items-center rounded-xl border border-white/15 text-white transition-colors hover:border-[#54f6c6] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-502" data-mp="l8g1aiv3883">
                  {muted ? <VolumeX className="h-4 w-4" data-id="element-503" data-mp="1oihn082qqkh8" /> : <Volume2 className="h-4 w-4" data-id="element-504" data-mp="1ojsqe6s9xq7t" />}
                </button>
              </div>

              <span className="font-mono text-[11px] tracking-[0.12em] text-white/60" data-id="element-505" data-mp="1jkpxfkpkjag7">{timecode(current)}</span>
              <input type="range" min={0} max={100} step={0.1} value={progress} onChange={onScrub} disabled={!item.videoUrl} aria-label="Seek" className="viewer-scrub h-1 min-w-[140px] flex-1 appearance-none bg-white/15 disabled:opacity-30" data-id="element-506" data-mp="1yhoysqttu728" />
              <span className="font-mono text-[11px] tracking-[0.12em] text-white/45" data-id="element-507" data-mp="2h63sjlvj3z6q">{timecode(duration)}</span>

              <div className="flex items-center gap-2" data-id="element-508" data-mp="2c93vu1kkk8sa">
                <button type="button" onClick={requestFullscreen} disabled={!item.videoUrl} title={item.videoUrl ? undefined : "No source file attached to this frame yet"} aria-label="Fullscreen" className="grid h-11 w-11 place-items-center rounded-xl border border-white/15 text-white transition-colors hover:border-[#54f6c6] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-509" data-mp="2q7lc5e85aj06">
                  <Maximize2 className="h-4 w-4" data-id="element-510" data-mp="30vbckc3cjtn1" />
                </button>
                <a href={links.sourceArchive} target="_blank" rel="noreferrer" data-cursor="Open" className="flex min-h-11 items-center gap-2 rounded-xl border border-white/15 px-4 font-mono text-[11px] uppercase tracking-[0.14em] text-white/75 transition-colors hover:border-[#54f6c6] hover:text-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-511" data-mp="2m3iys4j7annf">
                  Source <ExternalLink className="h-3.5 w-3.5" data-id="element-512" data-mp="28400e3g6yqob" />
                </a>
              </div>
            </div>}


            {/* Touch-friendly stepper, since the side arrows are desktop-only. */}
            <div className="mt-4 flex items-center gap-2 md:hidden" data-id="element-513" data-mp="8wb8n4yy2n8a">
              <button type="button" onClick={() => step(-1)} aria-label="Previous project" className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-white/15 font-mono text-[11px] uppercase tracking-[0.14em] text-white transition-colors hover:border-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-514" data-mp="4x2dpws64jb5">
                <ArrowLeft className="h-4 w-4" data-id="element-515" data-mp="nobm22q7d215" /> Prev
              </button>
              <button type="button" onClick={() => step(1)} aria-label="Next project" className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-white/15 font-mono text-[11px] uppercase tracking-[0.14em] text-white transition-colors hover:border-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-516" data-mp="in5wuevr3wwd">
                Next <ArrowRight className="h-4 w-4" data-id="element-517" data-mp="337ci0usxhj13" />
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45" data-id="element-518" data-mp="37z47f20clipn">
              <span data-id="element-519" data-mp="3bzxuxu9vovi0">{(item.tags ?? []).join(" · ")}</span>
              <span data-id="element-520" data-mp="3gyhiu0818hd0">
                {String(activeIndex + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                <span className="hidden md:inline" data-id="element-521" data-mp="3lzopfvyfvai3"> · ← → to move</span>
              </span>
            </div>
          </footer>
        </motion.div>}
    </AnimatePresence>;
}